import Auth, { CognitoUser } from "@aws-amplify/auth";
import { useContext } from "react";
import { selector, useRecoilValue } from "recoil";
import { AppContext } from "./AppContext";
import { appContextState, clientState, queryStates } from "./client";
import { useLoadingText } from "./components/Loading";
import { TERMS_OF_USE_VERSION } from "./constants";
import {
  CreateOrLinkUserDocument,
  CreateOrLinkUserMutation,
  CreateOrLinkUserMutationVariables,
  Maybe,
  Persona,
  ProfileScreenDocument,
  ProfileScreenQuery,
  ProfileScreenQueryVariables,
} from "./types";

export type OnboardingPersona =
  | Persona.ServiceProvider
  | Persona.PropertyManager;

export type OnboardingProfileWithPersona = Omit<
  NonNullable<OnboardingProfile>,
  "persona"
> & { persona: OnboardingPersona };

export type OnboardedProfile = Readonly<
  Omit<NonNullable<ProfileScreenQuery["profile"]>, "persona"> & {
    persona: Persona;
  }
>;

export type OnboardingProfile = Maybe<
  Readonly<
    Omit<NonNullable<ProfileScreenQuery["profile"]>, "persona"> & {
      persona: Maybe<OnboardingPersona>;
    }
  >
>;

type OnboardingProfileState =
  | {
      readonly doneOnboarding: true;
      readonly profile: OnboardedProfile;
    }
  | {
      readonly doneOnboarding: false;
      readonly profile: OnboardingProfile;
    };

export const [profileQueryState, useWatchProfileQuery] = queryStates<
  ProfileScreenQueryVariables,
  ProfileScreenQuery
>(ProfileScreenDocument);

const isOnboardingNeeded = (
  profile: NonNullable<Readonly<ProfileScreenQuery["profile"]>>
): OnboardingProfileState => {
  if (profile.persona === Persona.Metrologist) {
    return {
      doneOnboarding: true,
      profile: {
        ...profile,
        persona: profile.persona,
      },
    };
  }

  if (
    profile.persona &&
    profile.name &&
    profile.companyName &&
    profile.agreedToTermsOfUse &&
    profile.termsOfUseVersion === TERMS_OF_USE_VERSION
  ) {
    return {
      doneOnboarding: true,
      profile: {
        ...profile,
        persona: profile.persona,
      },
    };
  }

  return {
    doneOnboarding: false,
    profile: {
      ...profile,
      persona: profile.persona,
    },
  };
};

const currentUserProfileState = selector<ProfileScreenQuery>({
  key: "CurrentUserProfile",
  get: ({ get }) => {
    const appContext = get(appContextState);
    if (!appContext) throw new Error("No AppContext configured");
    return profileQueryState({ username: appContext.username });
  },
});

const switchToCognitoUserState = selector<boolean>({
  key: "SwitchToCognitoUser",
  get: ({ get }) => {
    const profile = get(currentUserProfileState);
    // If the profile exists, then no need to check for user creation and linking
    if (profile.profile) return false;

    const client = get(clientState);
    if (!client) throw new Error("No client exists");

    return client
      .mutate<CreateOrLinkUserMutation, CreateOrLinkUserMutationVariables>({
        mutation: CreateOrLinkUserDocument,
      })
      .then(({ data, errors }) => {
        if (errors) throw errors[0];
        if (!data) throw new Error("No data was returned");

        const { token, username } = data.createOrLinkUser;
        if (!token) return false;

        return Auth.signIn(username)
          .then((user: CognitoUser & { challengeName?: string }) => {
            if (user.challengeName === "CUSTOM_CHALLENGE") {
              return Auth.sendCustomChallengeAnswer(user, token);
            }
            return user;
          })
          .then((_: CognitoUser) => {
            return false;
            // After re-authenticating, the need for a profile will be re-checked
          });
      });
  },
});

export const onboardingProfileState = selector<OnboardingProfileState>({
  key: "OnboardingProfile",
  get: ({ get }): OnboardingProfileState => {
    const profile = get(currentUserProfileState);
    if (profile.profile) return isOnboardingNeeded(profile.profile);

    get(switchToCognitoUserState);

    return {
      doneOnboarding: false,
      profile: null,
    };
  },
});

export const useOnboardingProfile = () => {
  useLoadingText("Loading profile...");
  const appContext = useContext(AppContext);

  useWatchProfileQuery({ username: appContext.username });
  const onboardingProfile = useRecoilValue(onboardingProfileState);

  return onboardingProfile;
};
